/** Загружает и синхронизирует серверный проект по UTC-дате изменения. */
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { apiHooks } from "@/api";
import { useCustomFurnitureStore } from "@/shared/catalog";
import { useOnlineStatus } from "@/shared/hooks";
import {
	createProject,
	type Project,
	type ProjectSaveResult,
	useEditorStore,
	useProjectStore,
	setUnderlayPersistenceEnabled,
	authUserId,
	useAuthStore,
} from "@/shared/store";
import { getLocalProject, putLocalProject } from "./localProjectCache";
import { dehydrateProject, parseProjectData, projectFile } from "./serialization";
import { ProjectSyncState } from "../types";

const AUTO_SAVE_INTERVAL_MS = 60_000;

const payload = (project: Project, lastModifiedDate: string) => ({
	data: JSON.parse(JSON.stringify(projectFile(project, lastModifiedDate))) as Record<
		string,
		unknown
	>,
	lastModifiedDate,
});

const remoteProject = (
	projectId: string,
	data: { data: unknown; description: string | null; name: string },
): Project => ({
	...(parseProjectData(data.data) ?? createProject()),
	description: data.description ?? "",
	id: projectId,
	name: data.name,
});

export const useProjectSync = (projectId: string | null) => {
	const isOnline = useOnlineStatus();
	const userId = useAuthStore((store) => authUserId(store.accessToken));
	const { refetch: fetchRemoteProject } = apiHooks.projects.useProjectData(
		userId,
		projectId,
		false,
	);
	const { execute: saveProjectData } = apiHooks.projects.useSaveProjectData();
	const [state, setState] = useState<ProjectSyncState>(ProjectSyncState.Loading);
	const [isReadOnly, setIsReadOnly] = useState(false);
	const loadProject = useProjectStore((store) => store.loadProject);
	const setSaveProject = useEditorStore((store) => store.setSaveProject);
	const baseline = useRef<string | null>(null);

	const flushChanges = useEffectEvent(async (): Promise<ProjectSaveResult> => {
		if (state === ProjectSyncState.Loading || isReadOnly || projectId === null) return "local";
		// Демо — чужой пример, а не проект пользователя: его не сохраняем, иначе он затрёт план
		if (useProjectStore.getState().demo) return "local";
		const currentProject = dehydrateProject(useProjectStore.getState().project);
		if (currentProject.id !== projectId) return "local";
		const serialized = JSON.stringify(currentProject);
		// Правок нет — на сервере уже то же, если прошлая отправка удалась
		if (baseline.current === null || baseline.current === serialized)
			return state === ProjectSyncState.Offline ? "local" : "synced";

		const lastModifiedDate = new Date().toISOString();
		await putLocalProject({
			lastModifiedDate,
			project: currentProject,
			projectId,
			syncStatus: "pending",
		});
		baseline.current = serialized;
		if (!isOnline) {
			setState(ProjectSyncState.Offline);
			return "local";
		}
		const response = await saveProjectData([
			{ id: projectId },
			payload(currentProject, lastModifiedDate),
		]);
		if (response.data === null) {
			setState(ProjectSyncState.Offline);
			return "local";
		}
		await putLocalProject({
			lastModifiedDate: response.data.lastModifiedDate,
			project: currentProject,
			projectId,
			syncStatus: "synced",
		});
		setState(ProjectSyncState.Ready);
		return "synced";
	});

	useEffect(() => {
		let active = true;
		const load = async () => {
			useCustomFurnitureStore.getState().setActiveProjectId(projectId);
			if (projectId === null) {
				setIsReadOnly(false);
				baseline.current = null;
				if (!useProjectStore.getState().demo) useProjectStore.getState().loadEmptyDemo();
				setState(ProjectSyncState.Ready);
				return;
			}

			setState(ProjectSyncState.Loading);
			setUnderlayPersistenceEnabled(true);
			void useCustomFurnitureStore.getState().loadCustomFurniture(projectId);

			const local = await getLocalProject(projectId);
			if (!active) return;
			if (!isOnline) {
				if (local === null) {
					setState(ProjectSyncState.Error);
					return;
				}
				loadProject(local.project);
				baseline.current = JSON.stringify(dehydrateProject(local.project));
				setState(ProjectSyncState.Offline);
				return;
			}

			const { data: response, isError } = await fetchRemoteProject({ cancelRefetch: false });
			if (!active) return;
			if (isError || response?.data == null) {
				if (local !== null) {
					loadProject(local.project);
					baseline.current = JSON.stringify(dehydrateProject(local.project));
					setState(ProjectSyncState.Offline);
				} else {
					setState(ProjectSyncState.Error);
				}
				return;
			}

			setIsReadOnly(response.data.isReadOnly);
			if (
				local !== null &&
				!response.data.isReadOnly &&
				new Date(local.lastModifiedDate).getTime() >
					new Date(response.data.lastModifiedDate).getTime()
			) {
				loadProject(local.project);
				baseline.current = JSON.stringify(dehydrateProject(local.project));
				const saved = await saveProjectData([
					{ id: projectId },
					payload(local.project, local.lastModifiedDate),
				]);
				if (!active) return;
				if (saved.data !== null) {
					await putLocalProject({
						lastModifiedDate: saved.data.lastModifiedDate,
						project: dehydrateProject(local.project),
						projectId,
						syncStatus: "synced",
					});
				}
			} else {
				const nextProject = remoteProject(projectId, response.data);
				loadProject(nextProject);
				baseline.current = JSON.stringify(dehydrateProject(nextProject));
				if (!response.data.isReadOnly) {
					await putLocalProject({
						lastModifiedDate: response.data.lastModifiedDate,
						project: dehydrateProject(nextProject),
						projectId,
						syncStatus: "synced",
					});
				}
			}
			setState(ProjectSyncState.Ready);
		};

		void load();
		return () => {
			active = false;
		};
	}, [fetchRemoteProject, isOnline, loadProject, projectId, saveProjectData]);

	// Кнопка «Сохранить» и Ctrl+S отправляют серверный проект сразу, не дожидаясь автосохранения
	useEffect(() => {
		if (projectId === null) return;
		setSaveProject(() => flushChanges());
		return () => setSaveProject(null);
	}, [projectId, setSaveProject]);

	useEffect(() => {
		const interval = window.setInterval(() => void flushChanges(), AUTO_SAVE_INTERVAL_MS);
		return () => {
			window.clearInterval(interval);
			void flushChanges();
		};
	}, []);

	return { isReadOnly, state };
};
